/**
 * 中国时区(UTC+8)时间工具
 *
 * 背景：后端返回的时间多为不带时区标识的“裸钟表时间”字符串（如 "2026-10-16 11:00:00"）。
 * 直接 new Date(str) 会按浏览器本地时区解析，在非东八区环境下会产生计时/展示误差。
 * 本工具统一按中国时区解析与格式化，保证任意浏览器时区下结果一致。
 */

// 中国时区标识
export const CHINA_TIME_ZONE = 'Asia/Shanghai'

// 中国时区与 UTC 的固定偏移（毫秒），中国无夏令时
const CHINA_UTC_OFFSET_MS = 8 * 60 * 60 * 1000

/**
 * 将时间值按中国时区(UTC+8)解析为绝对时间戳(ms)
 * 兼容：毫秒/秒级时间戳、Date 对象、带时区标识(Z 或 ±HH:mm)的字符串、不带时区的裸字符串(视为中国时间)
 * @param {string|number|Date} value 时间值
 * @returns {number|null} 时间戳(ms)，无法解析时返回 null
 */
export function parseAsChinaTime(value) {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) {
    const dateTs = value.getTime()
    return Number.isNaN(dateTs) ? null : dateTs
  }
  const raw = String(value).trim()
  // 纯数字时间戳（兼容秒级/毫秒级）
  if (/^\d+$/.test(raw)) {
    const ts = Number(raw)
    return raw.length <= 10 ? ts * 1000 : ts
  }
  // 已带时区标识（Z 或 ±HH:mm），按标准解析
  if (/(Z|[+-]\d{2}:?\d{2})$/i.test(raw)) {
    const ts = new Date(raw).getTime()
    return Number.isNaN(ts) ? null : ts
  }
  // 无时区标识的日期时间字符串，视为中国时间(UTC+8)
  const match = raw.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/)
  if (match) {
    const [, y, mo, d, h = '0', mi = '0', s = '0'] = match
    // 先按 UTC 构造，再减去 8 小时偏移，得到该“中国时间”对应的真实时间戳
    return Date.UTC(Number(y), Number(mo) - 1, Number(d), Number(h), Number(mi), Number(s)) - CHINA_UTC_OFFSET_MS
  }
  const ts = new Date(raw).getTime()
  return Number.isNaN(ts) ? null : ts
}

// 中国时区格式化器（复用以避免重复创建）
const chinaTimeFormatter = new Intl.DateTimeFormat('zh-CN', {
  timeZone: CHINA_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
  hour12: false,
})

/**
 * 将时间值按中国时区(UTC+8)格式化为 "YYYY-MM-DD HH:mm:ss"
 * @param {string|number|Date} value 时间值
 * @param {string} [fallback='-'] 无法解析时的兜底展示
 * @returns {string}
 */
export function formatChinaTime(value, fallback = '-') {
  const ts = parseAsChinaTime(value)
  if (ts === null) return value ? String(value) : fallback
  const parts = chinaTimeFormatter.formatToParts(new Date(ts))
  const get = (type) => parts.find((p) => p.type === type)?.value || ''
  const hour = get('hour') === '24' ? '00' : get('hour')
  return `${get('year')}-${get('month')}-${get('day')} ${hour}:${get('minute')}:${get('second')}`
}

/**
 * 判断当前浏览器是否处于中国时区(UTC+8)
 * 说明：按偏移量判断，凡 UTC+8 环境（含新加坡/珀斯等）本地时间即等于北京时间，无需额外标注
 * @returns {boolean}
 */
export function isChinaTimeZone() {
  return new Date().getTimezoneOffset() === -(CHINA_UTC_OFFSET_MS / 60000)
}

/**
 * 获取本地时区相对 UTC 的偏移文案，如 "UTC+8"、"UTC-4"、"UTC+5:30"
 * @returns {string}
 */
export function getLocalUtcOffsetText() {
  const offsetMin = -new Date().getTimezoneOffset()
  const sign = offsetMin < 0 ? '-' : '+'
  const abs = Math.abs(offsetMin)
  const h = Math.floor(abs / 60)
  const m = abs % 60
  return `UTC${sign}${h}${m ? ':' + String(m).padStart(2, '0') : ''}`
}

export default {
  CHINA_TIME_ZONE,
  parseAsChinaTime,
  formatChinaTime,
  isChinaTimeZone,
  getLocalUtcOffsetText,
}
